import React from 'react'
import { StyleSheet, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { Icon, Text } from '@components'
import { IconName } from '@components'
import { useLanguage } from '@context'
import { SelectionItem } from '@utils'
import { getComponentsStyles } from './styles'


export type ContactItem = {
  type: 'mail' | 'website' | 'phone'
  label: string
  value: string
  iconName: IconName
}

export type HashtagCategory = {
  category: string
  hashtags: string[]
}

// Type union pour les différents types d'items
export type ProfileSectionItem = SelectionItem | ContactItem | HashtagCategory

// Props du composant générique
export type ProfileSectionProps = {
  title: string
  sectionType: 'places' | 'contacts' | 'hashtags'
  items: ProfileSectionItem[]
  noEdit?: boolean
  onEdit?: () => void
}

export const ProfileSection = ({ 
  title, 
  sectionType, 
  items, 
  noEdit = false,
  onEdit 
}: ProfileSectionProps): React.ReactElement => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyle(theme)
  const componentsStyles = getComponentsStyles(theme)

  const renderPlaceItem = (item: SelectionItem, index: number) => (
    <View 
      key={index} 
      style={[
        styles.sectionRow, 
        index === items.length - 1 && styles.lastSectionRow
      ]}
    >
      <View style={styles.leftContent}>
        <Text type="bodyBold" color={theme.colors.secondary}>
          {item.name}
        </Text>
      </View>
      <Icon 
        name='globe' 
        color={theme.colors.primary} 
        size={theme.iconSize.large}
      />
    </View>
  )

  const renderContactItem = (item: ContactItem, index: number) => (
    <View 
      key={index} 
      style={[
        styles.sectionRow, 
        index === items.length - 1 && styles.lastSectionRow
      ]}
    >
      <View style={styles.leftContent}>
        <Text type="bodySmall" color={theme.colors.greyBlue} style={styles.sectionTitle}>
          {item.label}
        </Text>
        <Text type="bodyBold" color={theme.colors.secondary}>
          {item.value}
        </Text>
      </View>
      <Icon 
        name={item.iconName} 
        color={theme.colors.primary} 
        size={theme.iconSize.large}
      />
    </View>
  )

  const renderHashtagItem = (item: HashtagCategory, index: number) => (
    <View 
      key={index} 
      style={[
        styles.sectionRow, 
        index === items.length - 1 && styles.lastSectionRow
      ]}
    >
      <View style={styles.leftContent}>
        <Text type="bodySmall" color={theme.colors.greyBlue} style={styles.sectionTitle}>
          {item.category}
        </Text>
        <View style={styles.hashtagsContainer}>
          {item.hashtags.map((hashtag, hashtagIndex) => (
            <View key={hashtagIndex} style={styles.hashtag}>
              <Text color={theme.colors.primary} type="bodyExtraSmall">#{hashtag}</Text>
            </View>
          ))}
        </View>
      </View>
      <Icon 
        name="heart" 
        color={theme.colors.primary} 
        size={theme.iconSize.large}
      />
    </View>
  )

  const renderItem = (item: ProfileSectionItem, index: number) => {
    switch (sectionType) {
      case 'places':
        return renderPlaceItem(item as SelectionItem, index)
      case 'contacts':
        return renderContactItem(item as ContactItem, index)
      case 'hashtags':
        return renderHashtagItem(item as HashtagCategory, index)
      default:
        return null
    }
  }

  const renderEmptyState = () => {
    const emptyMessages = {
      places: t('profileScreen.noLivingPlaceAdded'),
      contacts: t('profileScreen.noContactInfoAdded'),
      hashtags: t('profileScreen.noHashtagAdded')
    }

    return (
      <View style={componentsStyles.emptyState}>
        <Text type="bodySmall" color={theme.colors.greyBlue} style={componentsStyles.emptyText}>
          {emptyMessages[sectionType]}
        </Text>
      </View>
    )
  }

  return (
    <View style={styles.container}>
      <View style={styles.topContainer}>
        <Text type="bodyBold" color={theme.colors.primary} style={styles.title}>
          {title}
        </Text>
        {!noEdit && <Icon
          name="edit"
          color={theme.colors.greyBlue}
          size={theme.iconSize.medium}
          containerStyle={styles.editIcon}
          onPress={onEdit}
        />}
      </View>

      <View style={styles.sectionContainer}>
        {items.length === 0 ? (
          renderEmptyState()
        ) : (
          items.map((item, index) => (
            <React.Fragment key={index}>
              {renderItem(item, index)}
            </React.Fragment>
          ))
        )}
      </View>
    </View>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: {
      paddingHorizontal: theme.spacing.xl,
    },
    topContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: theme.spacing.sm,
      marginTop: theme.spacing.md
    },
    title: {
      margin: 0,
    },
    editIcon: {
      padding: theme.spacing.sm,
    },
    sectionContainer: {
      backgroundColor: theme.colors.white,
      borderWidth: 1,
      borderColor: theme.colors.grey,
      borderRadius: theme.border.large,
      paddingHorizontal: theme.spacing.lg,
      paddingVertical: theme.spacing.lg,
      marginBottom: theme.spacing.lg,
    },
    sectionRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: theme.spacing.lg,
    },
    lastSectionRow: {
      marginBottom: 0,
    },
    leftContent: {
      flex: 1,
    },
    sectionTitle: {
      marginBottom: theme.spacing.xs,
    },
    hashtagsContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      marginTop: theme.spacing.xs,
    },
    hashtag: {
      marginRight: theme.spacing.sm,
      marginBottom: theme.spacing.xs,
      borderWidth: 1,
      borderRadius: theme.border.extraLarge,
      borderColor: theme.colors.primary,
      paddingVertical: theme.spacing.xxs,
      paddingHorizontal: theme.spacing.xs,
    },
  }) 